/**
 * Keys stored in the backend User.preferences JSON (PATCH /auth/me/).
 */

export const MAIN_CONTENT_WIDTH_KEY = "main_content_width" as const;

export type MainContentWidthPreference = "centered" | "wide";

/**
 * **Wide by default.** The centred `max-w-7xl` box was chosen for reading
 * width, which is the right default for prose and the wrong one here: this is a
 * console of dense tables, multi-column charts and a flow card whose service
 * column is the widest thing on the page. Capping it left a third of a
 * 1920-pixel display empty while the content inside scrolled.
 *
 * `centered` remains available and is still honoured wherever a user has
 * chosen it -- only the answer for someone who has never expressed a preference
 * changes.
 */
export const DEFAULT_MAIN_CONTENT_WIDTH: MainContentWidthPreference = "wide";

export function parseMainContentWidth(
  preferences: unknown,
): MainContentWidthPreference {
  if (!preferences || typeof preferences !== "object") {
    return DEFAULT_MAIN_CONTENT_WIDTH;
  }
  const raw = (preferences as Record<string, unknown>)[MAIN_CONTENT_WIDTH_KEY];
  // Only an explicit "centered" opts out. An absent key is not a choice, so it
  // takes the default rather than the narrow box it used to fall back to.
  if (raw === "centered") return "centered";
  if (raw === "wide") return "wide";
  return DEFAULT_MAIN_CONTENT_WIDTH;
}

export const REPORTING_CURRENCY_KEY = "currency" as const;

/**
 * Currencies cost can be reported in.
 *
 * This mirrors `REPORTING_CURRENCIES` in the backend settings, and the two have
 * to agree: cost is pre-converted into exactly these at ingestion, so a value
 * the backend does not know has no column to read and the request is rejected.
 * Adding one here without adding it there produces a 400, not a fallback.
 */
export const REPORTING_CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"] as const;

export type ReportingCurrency = (typeof REPORTING_CURRENCIES)[number];

export const DEFAULT_REPORTING_CURRENCY: ReportingCurrency = "USD";

/**
 * Is this one of the currencies **this client** knows about?
 *
 * A membership test, used for a *saved preference*: a stored value the client
 * cannot render has to fall back rather than be trusted.
 *
 * Note the comparison and the narrowing must agree. This tests
 * `value.toUpperCase()` while narrowing the raw value, so `"cad"` passes here
 * and callers must normalize before storing it, or a later `includes("CAD")`
 * fails and silently reverts to the default. `parseReportingCurrency` does that.
 */
export function isReportingCurrency(value: unknown): value is ReportingCurrency {
  return (
    typeof value === "string" &&
    (REPORTING_CURRENCIES as readonly string[]).includes(value.toUpperCase())
  );
}

/**
 * Is this a well-formed ISO 4217 code?
 *
 * A **shape** test, for validating the list the server publishes. Checking that
 * list against `REPORTING_CURRENCIES` instead would clamp it to the local
 * constant, so a deployment adding EUR would have it published, accepted by the
 * backend, and then dropped here: exactly the drift the published list exists to
 * remove, reintroduced by the guard meant to prevent it.
 */
export function isCurrencyCode(value: unknown): value is ReportingCurrency {
  return typeof value === "string" && /^[A-Z]{3}$/.test(value);
}

/**
 * The user's saved reporting currency, or the default.
 *
 * Falls back rather than throwing: a preference blob is free-form JSON that an
 * older client or a hand-edited profile could have put anything into, and a
 * dashboard that renders in dollars is better than one that does not render.
 * The backend refuses an unsupported value on write, so this should not happen
 * for anything saved through the app.
 */
export function parseReportingCurrency(preferences: unknown): ReportingCurrency {
  if (!preferences || typeof preferences !== "object") {
    return DEFAULT_REPORTING_CURRENCY;
  }
  const raw = (preferences as Record<string, unknown>)[REPORTING_CURRENCY_KEY];
  return isReportingCurrency(raw)
    ? (raw.toUpperCase() as ReportingCurrency)
    : DEFAULT_REPORTING_CURRENCY;
}
